import type { AdminCatalogEvent, AdminCatalogRevertResult } from '@steado/admin-contract/catalog-history';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { TabSkeleton } from '@/components/panel/panels';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { cn } from '@/lib/utils';
import { TabError } from '@/screens/user-detail/TabError';

import {
  invalidateCatalog,
  revertChange,
  revertChanges,
  subjectEventsQueryOptions,
  subjectHistoryQueryOptions,
} from './catalog-history-query';
import { SOURCE_LABELS, actorName, eventTitle, requestIdOf, withoutRequestId } from './event-copy';
import { BeforeAfter, FilterSelect, Figures, SourceList, SourcesButton, StatusBanner } from './history-parts';
import {
  groupEvents,
  labelWhen,
  nutritionBlock,
  revertAllCount,
  rowStates,
  withNameLists,
  type FieldGroup,
  type HistoryEvent,
  type HistoryRow,
  type RevertTarget,
  type RowState,
} from './history-model';
import { SourceChip } from './SourceChip';

export interface HistoryTabProps {
  readonly subject: 'food' | 'exercise';
  readonly slug: string;
  /** What the record is called, in sentences: "since Kulfi was added". */
  readonly name: string;
  /** The names it answers to now, from which History works back each event's list. */
  readonly currentNames: readonly string[];
  /** The title of the event that added the record: "Added from INDB". */
  readonly addedTitle: string;
  /** Opens the Catalog changes screen on an event. */
  readonly onOpenEvent: (key: string) => void;
  /** Opens the food request an event approved, or whose approval it reverted. */
  readonly onOpenRequest?: ((id: string) => void) | undefined;
}

const ANY = '__any__';

const FIELD_FILTERS: Readonly<Record<'food' | 'exercise', readonly { value: string; label: string }[]>> = {
  food: [
    { value: ANY, label: 'All fields' },
    { value: 'nutrition', label: 'Nutrition' },
    { value: 'portions', label: 'Portions' },
    { value: 'names', label: 'Names' },
    { value: 'allergens', label: 'Allergens' },
  ],
  exercise: [
    { value: ANY, label: 'All fields' },
    { value: 'names', label: 'Names' },
    { value: 'other', label: 'Details' },
  ],
};

/** What a press of Revert sends, and what to say once it lands. */
interface RevertRequest {
  readonly key: string;
  readonly run: () => Promise<AdminCatalogRevertResult>;
  readonly done: string;
}

/**
 * Every change to one food or exercise, newest first: grouped by the action
 * that made it, each field before and after, with Revert per field, per
 * nutrition block and per action.
 *
 * Revert is offered only where the server would accept it, and where it
 * would not, the button stays visible and says why beside it
 * (`aria-describedby`): a field changed since (newer wins), or a nutrition
 * figure, which goes back with the rest of its block. Every revert asks first
 * in an inline `alertdialog`, and a refusal from the server (409, 422) is
 * shown there rather than closing it.
 */
export function HistoryTab({
  subject,
  slug,
  name,
  currentNames,
  addedTitle,
  onOpenEvent,
  onOpenRequest,
}: HistoryTabProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const history = useInfiniteQuery(subjectHistoryQueryOptions(api, subject, slug));
  const meta = useQuery(subjectEventsQueryOptions(api, subject, slug));

  const [field, setField] = useState<string>(ANY);
  const [source, setSource] = useState<string>(ANY);
  const [who, setWho] = useState<string>(ANY);
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<{ key: string; message: string } | null>(null);
  const [last, setLast] = useState<string | null>(null);
  const [openSources, setOpenSources] = useState<ReadonlySet<string>>(new Set());

  const changes = useMemo(() => history.data?.pages.flatMap((page) => page.items) ?? [], [history.data]);
  const events = useMemo(
    () => withNameLists(groupEvents(changes, subject), currentNames),
    [changes, subject, currentNames],
  );
  const metaByKey = useMemo(
    () => new Map((meta.data ?? []).map((event): [string, AdminCatalogEvent] => [event.key, event])),
    [meta.data],
  );

  const revert = useMutation({
    mutationFn: (request: RevertRequest) => request.run(),
    onSuccess: (_result, request) => {
      setPending(null);
      setError(null);
      setLast(request.done);
      invalidateCatalog(queryClient);
    },
    onError: (failure, request) => {
      setError({ key: request.key, message: failure.message });
    },
    retry: false,
  });

  const ask = (key: string) => {
    setPending(key);
    setError(null);
  };
  const cancel = () => {
    setPending(null);
    setError(null);
  };

  if (history.isPending) return <TabSkeleton rows={4} />;
  if (history.isError) {
    return <TabError message={history.error.message} onRetry={() => void history.refetch()} />;
  }

  const actors = new Map<string, string>();
  for (const event of events) {
    actors.set(event.actor?.id ?? 'system', actorName(event.actor));
  }
  const visible = events
    .filter((event) => source === ANY || event.kind === source)
    .filter((event) => who === ANY || (event.actor?.id ?? 'system') === who)
    .map((event) => ({
      event,
      rows: field === ANY ? event.rows : event.rows.filter((row) => row.group === (field as FieldGroup)),
    }))
    .filter(({ rows }) => rows.length > 0);

  const values = events.reduce((sum, event) => sum + event.rows.length, 0);
  const creation = events.find((event) => event.rows.some((row) => row.isCreation));

  return (
    <div className="flex flex-col gap-3.5">
      {last === null ? null : <StatusBanner>{last}</StatusBanner>}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <p className="min-w-[16rem] flex-1 text-[13px] text-muted-foreground">
          <span className="font-medium text-foreground">
            <Figures text={`${String(events.length)} changes, ${String(values)} values`} />
          </span>
          {creation === undefined
            ? ''
            : ` since ${name} was added on ${labelWhen(creation.at).replace(/ \d\d:\d\d$/, '')}`}
          {' · newest first'}
        </p>
        <FilterSelect label="Field" value={field} options={FIELD_FILTERS[subject]} onChange={setField} />
        <FilterSelect
          label="Source"
          value={source}
          options={[
            { value: ANY, label: 'All sources' },
            ...Object.entries(SOURCE_LABELS).map(([value, label]) => ({ value, label })),
          ]}
          onChange={setSource}
        />
        <FilterSelect
          label="Who"
          value={who}
          options={[
            { value: ANY, label: 'Anyone' },
            ...[...actors.entries()].map(([value, label]) => ({ value, label })),
          ]}
          onChange={setWho}
        />
      </div>

      <section
        aria-labelledby="history-title"
        className="overflow-hidden rounded-xl border border-border bg-card"
      >
        <header className="flex flex-wrap items-center gap-x-2.5 gap-y-1 px-[18px] pt-3.5 pb-3">
          <h3 id="history-title" className="text-[15px] font-semibold text-foreground">
            History
          </h3>
          <span className="text-[12.5px] text-muted-foreground">
            A revert is saved as a new change. If a field changes again later, the newer change wins.
          </span>
        </header>
        <div className="overflow-x-auto">
          <div className="min-w-[760px]">
            <div className="grid grid-cols-[minmax(170px,0.8fr)_minmax(0,1.6fr)_150px] gap-3 border-t border-border bg-secondary px-[18px] py-2 text-[11px] font-semibold tracking-[0.055em] text-muted-foreground uppercase">
              <span>Field</span>
              <span>Before → after</span>
              <span className="text-right">Revert</span>
            </div>
            {visible.length === 0 ? (
              <p className="border-t border-border px-[18px] py-6 text-[13px] text-muted-foreground">
                {events.length === 0 ? 'No changes recorded yet.' : 'No changes match these filters.'}
              </p>
            ) : null}
            {visible.map(({ event, rows }) => (
              <EventBlock
                key={event.key}
                event={event}
                rows={rows}
                states={rowStates(event, changes, subject)}
                title={eventTitle({
                  kind: event.kind,
                  source: event.source,
                  event: metaByKey.get(event.key),
                  values: event.rows.length,
                  added: event.rows.some((row) => row.isCreation) ? addedTitle : undefined,
                })}
                requestId={
                  onOpenRequest === undefined ? null : requestIdOf(metaByKey.get(event.key), event.source)
                }
                onOpenRequest={onOpenRequest}
                canMutate={can.mutate}
                pending={pending}
                error={error}
                isReverting={revert.isPending}
                sourcesOpen={openSources.has(event.key)}
                onToggleSources={() => {
                  setOpenSources((current) => {
                    const next = new Set(current);
                    if (next.has(event.key)) next.delete(event.key);
                    else next.add(event.key);
                    return next;
                  });
                }}
                onOpenEvent={onOpenEvent}
                onAsk={ask}
                onCancel={cancel}
                onRevert={(request) => {
                  revert.mutate(request);
                }}
                revertTarget={(target) =>
                  target.fields === undefined
                    ? () => revertChange(api, target.changeId)
                    : () => revertChange(api, target.changeId, target.fields)
                }
                revertAll={(ids) => () => revertChanges(api, ids)}
              />
            ))}
          </div>
        </div>
        {history.hasNextPage ? (
          <div className="flex justify-center border-t border-border bg-secondary/40 px-[18px] py-2.5">
            <Button
              variant="outline"
              size="sm"
              disabled={history.isFetchingNextPage}
              onClick={() => void history.fetchNextPage()}
            >
              {history.isFetchingNextPage ? 'Loading…' : 'Show older changes'}
            </Button>
          </div>
        ) : null}
      </section>
    </div>
  );
}

interface EventBlockProps {
  readonly event: HistoryEvent;
  readonly rows: readonly HistoryRow[];
  readonly states: ReadonlyMap<string, RowState>;
  readonly title: string;
  readonly requestId: string | null;
  readonly onOpenRequest: ((id: string) => void) | undefined;
  readonly canMutate: boolean;
  readonly pending: string | null;
  readonly error: { key: string; message: string } | null;
  readonly isReverting: boolean;
  readonly sourcesOpen: boolean;
  readonly onToggleSources: () => void;
  readonly onOpenEvent: (key: string) => void;
  readonly onAsk: (key: string) => void;
  readonly onCancel: () => void;
  readonly onRevert: (request: RevertRequest) => void;
  readonly revertTarget: (target: RevertTarget) => () => Promise<AdminCatalogRevertResult>;
  readonly revertAll: (ids: readonly string[]) => () => Promise<AdminCatalogRevertResult>;
}

function EventBlock({
  event,
  rows,
  states,
  title,
  requestId,
  onOpenRequest,
  canMutate,
  pending,
  error,
  isReverting,
  sourcesOpen,
  onToggleSources,
  onOpenEvent,
  onAsk,
  onCancel,
  onRevert,
  revertTarget,
  revertAll,
}: EventBlockProps) {
  const when = labelWhen(event.at);
  // Inside a sentence: "from before today 10:43", "from before 3 Oct 12:30".
  const before = when.replace(/^(Today|Yesterday)/, (word) => word.toLowerCase());
  const block = nutritionBlock(event, states);
  const allCount = revertAllCount(event, states);
  const lastNutrition = rows.reduce((at, row, index) => (row.isNutrition ? index : at), -1);
  const allKey = `all:${event.key}`;
  const blockKey = `block:${event.key}`;
  const sourcesId = `sources-${event.key}`;
  const isBatch = event.kind === 'batch' && event.batchId !== null;
  const portionRows = event.rows.filter((row) => row.group === 'portions' && row.target.fields === undefined);

  return (
    <article aria-label={`${title}, ${when}`} className="border-t border-input/60">
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 bg-secondary/40 px-[18px] py-2.5">
        <SourceChip kind={event.kind} />
        <h4 className="text-[13px] font-semibold text-foreground">{title}</h4>
        <span className="text-[12.5px] text-muted-foreground">
          {actorName(event.actor)} · <Figures text={when} />
        </span>
        <span className="ml-auto flex flex-wrap items-center gap-1.5">
          {event.sources.length === 0 ? null : (
            <SourcesButton
              count={event.sources.length}
              open={sourcesOpen}
              onToggle={onToggleSources}
              controls={sourcesId}
            />
          )}
          {requestId !== null && onOpenRequest !== undefined ? (
            <Button
              variant="link"
              size="sm"
              className="h-8 px-1.5 text-info-fg"
              onClick={() => {
                onOpenRequest(requestId);
              }}
            >
              Open request
            </Button>
          ) : null}
          {isBatch ? (
            <Button
              variant="link"
              size="sm"
              className="h-8 px-1.5 text-info-fg"
              onClick={() => {
                onOpenEvent(event.key);
              }}
            >
              Open batch
            </Button>
          ) : null}
          {allCount > 0 ? (
            <Button
              variant="outline"
              size="sm"
              disabled={!canMutate || isReverting}
              title={canMutate ? undefined : 'Reverting requires the admin role.'}
              onClick={() => {
                onAsk(allKey);
              }}
            >
              Revert all {allCount}
            </Button>
          ) : null}
        </span>
      </div>
      {event.note === null || withoutRequestId(event.note) === `${title}.` ? null : (
        <p className="bg-secondary/40 px-[18px] pb-2.5 text-[12.5px] text-pretty text-muted-foreground">
          Note: {withoutRequestId(event.note)}
        </p>
      )}
      {sourcesOpen ? (
        <SourceList id={sourcesId} sources={event.sources} className="bg-secondary/40 px-[18px] pb-2.5" />
      ) : null}
      {pending === allKey ? (
        <Confirm
          label={`Revert all ${String(allCount)}`}
          text={`All ${String(allCount)} go back to their values from before ${before}. Saved as one new change in History.`}
          error={error?.key === allKey ? error.message : null}
          isPending={isReverting}
          onCancel={onCancel}
          onConfirm={() => {
            onRevert({
              key: allKey,
              run: revertAll(event.changes.map((change) => change.id)),
              done: `Reverted ${String(allCount)} values. Saved as the newest change in History.`,
            });
          }}
        />
      ) : null}

      {rows.map((row, index) => {
        const state = states.get(row.key) ?? { reason: null, blocked: false };
        const reasonId = `why-${row.key}`;
        const rowKey = `row:${row.key}`;
        const others = row.group === 'portions' && portionRows.length > 1 && portionRows.includes(row);
        return (
          <div key={row.key}>
            <div className="grid grid-cols-[minmax(170px,0.8fr)_minmax(0,1.6fr)_150px] items-center gap-3 border-t border-border px-[18px] py-2 text-[13px]">
              <span className="text-muted-foreground">{row.field}</span>
              <span className="flex min-w-0 flex-col gap-0.5">
                <BeforeAfter before={row.before} after={row.after} />
                {state.reason === null ? null : (
                  <span id={reasonId} className="text-[12px] text-pretty text-muted-foreground">
                    {state.reason}
                  </span>
                )}
              </span>
              <span className="flex justify-end">
                {state.reason === null ? (
                  <Button
                    variant="outline"
                    size="sm"
                    aria-label={`Revert ${row.field}`}
                    disabled={!canMutate || isReverting}
                    title={canMutate ? undefined : 'Reverting requires the admin role.'}
                    onClick={() => {
                      onAsk(rowKey);
                    }}
                  >
                    Revert
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    aria-label={`Revert ${row.field}`}
                    aria-disabled="true"
                    aria-describedby={reasonId}
                    className="cursor-not-allowed text-muted-foreground hover:bg-secondary"
                    onClick={(clicked) => {
                      clicked.preventDefault();
                    }}
                  >
                    Revert
                  </Button>
                )}
              </span>
            </div>
            {pending === rowKey ? (
              <Confirm
                label={`Revert ${row.short}`}
                text={`${row.field} goes back to ${row.before}. Saved as a new change in History.${
                  others ? ' The other portion changes from this save go back with it.' : ''
                }`}
                error={error?.key === rowKey ? error.message : null}
                isPending={isReverting}
                onCancel={onCancel}
                onConfirm={() => {
                  onRevert({
                    key: rowKey,
                    run: revertTarget(row.target),
                    done: `${row.field} is back to ${row.before}. Saved as the newest change in History.`,
                  });
                }}
              />
            ) : null}
            {block !== null && index === lastNutrition ? (
              <>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border bg-secondary/40 px-[18px] py-2">
                  <span className="min-w-[18rem] flex-1 text-[12.5px] text-pretty text-muted-foreground">
                    Energy, protein, carbohydrate, fat and fibre revert together, so energy always matches
                    what the macros add up to.
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!canMutate || isReverting}
                    title={canMutate ? undefined : 'Reverting requires the admin role.'}
                    onClick={() => {
                      onAsk(blockKey);
                    }}
                  >
                    Revert energy and macros ({block.count})
                  </Button>
                </div>
                {pending === blockKey ? (
                  <Confirm
                    label={`Revert energy and macros (${String(block.count)})`}
                    text={`Energy and macros go back to their values from before ${before}. Saved as one new change in History.`}
                    error={error?.key === blockKey ? error.message : null}
                    isPending={isReverting}
                    onCancel={onCancel}
                    onConfirm={() => {
                      onRevert({
                        key: blockKey,
                        run: revertTarget(block.target),
                        done: `Energy and macros are back to their values from before ${before}. Saved as the newest change in History.`,
                      });
                    }}
                  />
                ) : null}
              </>
            ) : null}
          </div>
        );
      })}
    </article>
  );
}

interface ConfirmProps {
  readonly label: string;
  readonly text: string;
  readonly error: string | null;
  readonly isPending: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

/** The inline question a revert asks before it runs, beside what it would revert. */
export function Confirm({ label, text, error, isPending, onCancel, onConfirm }: ConfirmProps) {
  return (
    <div
      role="alertdialog"
      aria-label={label}
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border bg-err-bg/40 px-[18px] py-2',
      )}
    >
      <span className="flex min-w-[18rem] flex-1 flex-col gap-0.5 text-[13px] text-pretty text-foreground">
        <Figures text={text} />
        {error === null ? null : (
          <span role="alert" className="text-[12.5px] font-medium text-err-fg">
            {error}
          </span>
        )}
      </span>
      <Button variant="ghost" size="sm" onClick={onCancel} disabled={isPending}>
        Cancel
      </Button>
      <Button
        variant="outline"
        size="sm"
        className="border-err-fg/30 text-err-fg hover:bg-err-bg hover:text-err-fg"
        onClick={onConfirm}
        disabled={isPending}
        // The question is the thing to answer: focus lands on it when it opens.
        autoFocus
      >
        {isPending ? 'Reverting…' : label}
      </Button>
    </div>
  );
}
